import { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  Typography,
  Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { trpc } from '../../lib/trpc';

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get plan chip color
 */
function getPlanColor(plan: string): 'default' | 'primary' | 'secondary' {
  switch (plan) {
    case 'PRO':
      return 'primary';
    case 'ENTERPRISE':
      return 'secondary';
    default:
      return 'default';
  }
}

/**
 * Admin users table with search, filter, and pagination
 */
export function AdminUsersTable() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<string>('');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Handle search with debounce
  const handleSearchChange = (value: string) => {
    setSearch(value);
    // Simple debounce using setTimeout
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 300);
  };

  // Query users
  const { data, isLoading, isError } = trpc.admin.getUsers.useQuery({
    page: page + 1,
    limit: rowsPerPage,
    search: debouncedSearch || undefined,
    plan: planFilter ? (planFilter as 'FREE' | 'PRO' | 'ENTERPRISE') : undefined,
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isError) {
    return (
      <Typography color="error">
        Failed to load users. Please try again.
      </Typography>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Filters */}
      <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Plan</InputLabel>
          <Select
            value={planFilter}
            label="Plan"
            onChange={(e) => {
              setPlanFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="FREE">Free</MenuItem>
            <MenuItem value="PRO">Pro</MenuItem>
            <MenuItem value="ENTERPRISE">Enterprise</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell align="center">Monitors</TableCell>
              <TableCell align="center">Channels</TableCell>
              <TableCell align="center">Status Pages</TableCell>
              <TableCell>Signed Up</TableCell>
              <TableCell align="center">Verified</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              // Loading skeleton
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton width={60} /></TableCell>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={40} /></TableCell>
                </TableRow>
              ))
            ) : data?.users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.plan}
                      size="small"
                      color={getPlanColor(user.plan)}
                    />
                  </TableCell>
                  <TableCell align="center">{user.monitorsCount}</TableCell>
                  <TableCell align="center">{user.channelsCount}</TableCell>
                  <TableCell align="center">{user.statusPagesCount}</TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={user.verified ? 'Yes' : 'No'}
                      size="small"
                      color={user.verified ? 'success' : 'default'}
                      variant={user.verified ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={data?.total ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}

export default AdminUsersTable;
